import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import JobFormDrawer from '../components/jobs/JobFormDrawer';
import { CategoryTag, SectionCard, StatCard, StatusPill } from '../components/common/ui';
import JobService from '../services/JobService';
import { fmtDate, fmtTimeRange } from '../utils/format';
import { STATUS } from '../styles/tokens';
import { useAuth } from '../contexts/AuthContext';
import { usePageRefresh } from '../components/layout/pullToRefresh';

function JobLine({ job, showDate }) {
    const navigate = useNavigate();
    return (
        <Box onClick={() => navigate(`/jobs/${job.id}`)} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.4, cursor: 'pointer',
            borderBottom: '1px solid #F5F7FA', '&:last-child': { borderBottom: 0 }, '&:hover': { bgcolor: '#F8FAFC' },
        }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 4, bgcolor: job.category?.color || '#CBD5E1', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: 13.5, color: '#0F172A' }}>{job.title}</Typography>
                <Typography noWrap sx={{ fontSize: 12, color: '#94A3B8' }}>
                    {!showDate && job.scheduledStart ? `${fmtTimeRange(job)} · ` : ''}{job.client?.name}{job.cadastralZone ? ` · ${job.cadastralZone}` : ''}{showDate ? ` · ${fmtDate(job.createdAt)}` : ''}
                </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}><CategoryTag category={job.category} /></Box>
            <StatusPill status={job.status} size="sm" />
        </Box>
    );
}

export default function DashboardPage() {
    const { t } = useTranslation();
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const load = () => {
        setError(false);
        return JobService.dashboard().then(setData).catch(() => setError(true));
    };
    // Not `useEffect(load, [])`: load returns its request (for pull-to-refresh), and an effect may only return a clean-up function.
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    usePageRefresh(load);

    const emptyLine = (text) => <Typography sx={{ px: 2.5, py: 3, fontSize: 13.5, color: '#94A3B8', textAlign: 'center' }}>{text}</Typography>;

    return (
        <Layout>
            <PageHeader
                title={user?.companyName || t('nav.dashboard')}
                subtitle={t('dashboard.subtitle')}
                action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDrawerOpen(true)}>{t('jobs.new')}</Button>}
            />

            {error ? (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                    <Typography sx={{ color: '#64748B', mb: 2 }}>{t('common.loadError')}</Typography>
                    <Button variant="outlined" onClick={load}>{t('common.retry')}</Button>
                </Box>
            ) : !data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <StatCard label={t('status.RECEIVED')} value={data.countsByStatus.RECEIVED} icon={<InboxOutlinedIcon />} color={STATUS.RECEIVED.color}
                                  sub={t('dashboard.waitingToStart')} onClick={() => navigate('/jobs')} />
                        <StatCard label={t('status.IN_PROGRESS')} value={data.countsByStatus.IN_PROGRESS} icon={<AutorenewIcon />} color={STATUS.IN_PROGRESS.color}
                                  sub={t('dashboard.beingWorkedOn')} onClick={() => navigate('/jobs')} />
                        {/* Field work done, elaborate not yet submitted to the cadastre: the office's to-do. */}
                        <StatCard label={t('dashboard.toSubmit')} value={data.awaitingSubmission} icon={<AssignmentTurnedInOutlinedIcon />} color="#7C3AED"
                                  sub={t('dashboard.toSubmitHint')} onClick={() => navigate('/jobs?stage=AWAITING_SUBMISSION')} />
                        <StatCard label={t('dashboard.doneThisMonth')} value={data.completedThisMonth} icon={<TaskAltIcon />} color={STATUS.DONE.color}
                                  sub={t('dashboard.doneTotal', { count: data.countsByStatus.DONE })} />
                        {isAdmin && (
                            // Number of jobs only: amounts are shown inside a job, after the password.
                            <StatCard label={t('dashboard.unpaid')} value={data.unpaidDoneCount} icon={<PaymentsOutlinedIcon />} color="#B45309"
                                      sub={t('dashboard.unpaidHint')} />
                        )}
                    </Box>

                    <Grid container spacing={2.5}>
                        <Grid size={{ xs: 12, lg: 6 }}>
                            <SectionCard title={<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EventAvailableOutlinedIcon sx={{ fontSize: 18, color: '#427898' }} />{t('planner.today')}</Box>}
                                         action={<Button size="small" onClick={() => navigate('/planner')}>{t('dashboard.openPlanner')}</Button>}>
                                {data.today.length ? data.today.map(j => <JobLine key={j.id} job={j} />) : emptyLine(t('dashboard.nothingToday'))}
                            </SectionCard>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 6 }}>
                            <SectionCard title={t('planner.tomorrow')}>
                                {data.tomorrow.length ? data.tomorrow.map(j => <JobLine key={j.id} job={j} />) : emptyLine(t('dashboard.nothingTomorrow'))}
                            </SectionCard>
                        </Grid>
                        {data.awaitingCadastre.length > 0 && (
                            <Grid size={12}>
                                <SectionCard title={(
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <HourglassTopIcon sx={{ fontSize: 18, color: '#B45309' }} />{t('dashboard.awaitingTitle')}
                                        <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: '#94A3B8' }}>· {t('dashboard.awaitingSub', { days: data.cadastreReminderDays })}</Box>
                                    </Box>
                                )}>
                                    {data.awaitingCadastre.map(j => (
                                        <Box key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.4, cursor: 'pointer',
                                            borderBottom: '1px solid #F5F7FA', '&:last-child': { borderBottom: 0 }, '&:hover': { bgcolor: '#F8FAFC' },
                                        }}>
                                            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 4, bgcolor: j.category?.color || '#CBD5E1', flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography noWrap sx={{ fontWeight: 700, fontSize: 13.5, color: '#0F172A' }}>{j.title}</Typography>
                                                <Typography noWrap sx={{ fontSize: 12, color: '#94A3B8' }}>
                                                    {[j.clientName, j.number, j.cadastreCaseNumber, t('dashboard.submittedOn', { date: fmtDate(j.submittedOn) })].filter(Boolean).join(' · ')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}><CategoryTag category={j.category} /></Box>
                                            <Box sx={{ fontSize: 12.5, fontWeight: 700, color: '#B45309', bgcolor: 'rgba(245,158,11,0.12)', px: 1, py: 0.25, borderRadius: '7px', whiteSpace: 'nowrap' }}>
                                                {t('dashboard.awaitingDays', { count: j.days })}
                                            </Box>
                                        </Box>
                                    ))}
                                </SectionCard>
                            </Grid>
                        )}
                        <Grid size={12}>
                            <SectionCard title={t('dashboard.recent')} action={<Button size="small" onClick={() => navigate('/jobs')}>{t('dashboard.allJobs')}</Button>}>
                                {data.recent.length ? data.recent.map(j => <JobLine key={j.id} job={j} showDate />) : emptyLine(t('dashboard.noJobsYet'))}
                            </SectionCard>
                        </Grid>
                    </Grid>
                </>
            )}

            <JobFormDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
                           onSaved={(job) => { setDrawerOpen(false); navigate(`/jobs/${job.id}`); }} />
        </Layout>
    );
}
